import React from 'react'
import TraumTagGuestFriendly from '../assets/icons/traum-tag/guest-friendly.svg'
import TraumTagPopularChoice from '../assets/icons/trending-up.svg'
import { getBasePath } from './miscellaneous-utils'

export enum TraumTagTypes {
    SUPER_HOST = 1,
    GUEST_FRIENDLY = 2,
    AWARD = 3,
    LUXE = 4,
    HIDDEN_GEM = 5,
    POPULAR_CHOICE = 6,
    DOG_FRIENDLY = 7,
    BEACH_HOLIDAY = 8,
    VALUE_FOR_MONEY = 9,
    SKI_FRIENDLY = 10,
    FAMILY_HAVEN = 11,
    FARM_HOLIDAY = 12,
    FISHING_HOLIDAY = 13,
    SEA_VIEWS = 14,
    HOLIDAY_BY_LAKE = 15,
    POOL_HOUSE = 16,
    WELLNESS_ESCAPE = 17,
    MOUNTAIN_VIEWS = 18,
}

interface TraumTagConfig {
    label: string
    Icon?: React.FC<React.SVGProps<SVGSVGElement>>
    imageUrl?: string
}

const TRAUM_TAGS: Record<number, TraumTagConfig> = {
    [TraumTagTypes.SUPER_HOST]: {
        label: 'traumTags.superHost',
        imageUrl: getBasePath('/assets/images/traum-tags/super-host.png'),
    },
    [TraumTagTypes.GUEST_FRIENDLY]: { label: 'traumTags.guestFriendly', Icon: TraumTagGuestFriendly },
    [TraumTagTypes.AWARD]: { label: 'traumTags.award', imageUrl: getBasePath('/assets/images/traum-tags/award.png') },
    [TraumTagTypes.LUXE]: { label: 'traumTags.luxe', imageUrl: getBasePath('/assets/images/traum-tags/luxus.png') },
    [TraumTagTypes.DOG_FRIENDLY]: {
        label: 'traumTags.dogFriendly',
        imageUrl: getBasePath('/assets/images/traum-tags/dog-friendly.png'),
    },
    [TraumTagTypes.BEACH_HOLIDAY]: {
        label: 'traumTags.beachHoliday',
        imageUrl: getBasePath('/assets/images/traum-tags/beach-holiday.png'),
    },
    [TraumTagTypes.VALUE_FOR_MONEY]: {
        label: 'traumTags.valueForMoney',
        imageUrl: getBasePath('/assets/images/traum-tags/value-for-money.png'),
    },
    [TraumTagTypes.SKI_FRIENDLY]: {
        label: 'traumTags.skiFriendly',
        imageUrl: getBasePath('/assets/images/traum-tags/ski-friendly.png'),
    },
    [TraumTagTypes.FAMILY_HAVEN]: {
        label: 'traumTags.familyHaven',
        imageUrl: getBasePath('/assets/images/traum-tags/family-haven.png'),
    },
    [TraumTagTypes.FARM_HOLIDAY]: {
        label: 'traumTags.farmHoliday',
        imageUrl: getBasePath('/assets/images/traum-tags/farm-holiday.png'),
    },
    [TraumTagTypes.FISHING_HOLIDAY]: {
        label: 'traumTags.fishingHoliday',
        imageUrl: getBasePath('/assets/images/traum-tags/fishing-holiday.png'),
    },
    [TraumTagTypes.SEA_VIEWS]: {
        label: 'traumTags.seaViews',
        imageUrl: getBasePath('/assets/images/traum-tags/sea-views.png'),
    },
    [TraumTagTypes.HOLIDAY_BY_LAKE]: {
        label: 'traumTags.holidayByLake',
        imageUrl: getBasePath('/assets/images/traum-tags/holiday-by-lake.png'),
    },
    [TraumTagTypes.POOL_HOUSE]: {
        label: 'traumTags.poolHouse',
        imageUrl: getBasePath('/assets/images/traum-tags/pool-house.png'),
    },
    [TraumTagTypes.WELLNESS_ESCAPE]: {
        label: 'traumTags.wellnessEscape',
        imageUrl: getBasePath('/assets/images/traum-tags/wellness-escape.png'),
    },
    [TraumTagTypes.MOUNTAIN_VIEWS]: {
        label: 'traumTags.mountainViews',
        imageUrl: getBasePath('/assets/images/traum-tags/mountain-views.png'),
    },
    [TraumTagTypes.POPULAR_CHOICE]: { label: 'traumTags.popularChoice', Icon: TraumTagPopularChoice },
    [TraumTagTypes.HIDDEN_GEM]: {
        label: 'traumTags.hiddenGem',
        imageUrl: getBasePath('/assets/images/traum-tags/tressure.png'),
    },
}

const PAID_BADGE_TAG_IDS = new Set([
    TraumTagTypes.SUPER_HOST,
    TraumTagTypes.GUEST_FRIENDLY,
    TraumTagTypes.AWARD,
    TraumTagTypes.LUXE,
    TraumTagTypes.DOG_FRIENDLY,
    TraumTagTypes.BEACH_HOLIDAY,
    TraumTagTypes.VALUE_FOR_MONEY,
    TraumTagTypes.SKI_FRIENDLY,
    TraumTagTypes.FAMILY_HAVEN,
    TraumTagTypes.FARM_HOLIDAY,
    TraumTagTypes.FISHING_HOLIDAY,
    TraumTagTypes.SEA_VIEWS,
    TraumTagTypes.HOLIDAY_BY_LAKE,
    TraumTagTypes.POOL_HOUSE,
    TraumTagTypes.WELLNESS_ESCAPE,
    TraumTagTypes.MOUNTAIN_VIEWS,
])

function isPaidBadgeTag(id: number): boolean {
    return PAID_BADGE_TAG_IDS.has(id)
}

export interface TraumTagData {
    id: number
    label: string
    Icon?: React.FC<React.SVGProps<SVGSVGElement>>
    imageUrl?: string
}

export interface CategorizedTraumTags {
    badgeTraumTags: TraumTagData[]
    popularChoiceTag: TraumTagData | undefined
    bottomTraumTags: TraumTagData[]
    hasBottomTags: boolean
}

export function getTraumTagsData(traumTagIds: number[] = []): CategorizedTraumTags {
    if (traumTagIds.length === 0) {
        return {
            badgeTraumTags: [],
            popularChoiceTag: undefined,
            bottomTraumTags: [],
            hasBottomTags: false,
        }
    }

    const badgeTraumTags: TraumTagData[] = []
    const bottomTraumTags: TraumTagData[] = []
    let popularChoiceTag: TraumTagData | undefined

    for (const id of traumTagIds) {
        const tag = TRAUM_TAGS[id]
        if (!tag) {
            continue
        }

        const traumTagData: TraumTagData = {
            id,
            label: tag.label,
            Icon: tag.Icon,
            imageUrl: tag.imageUrl,
        }

        if (isPaidBadgeTag(id)) {
            badgeTraumTags.push(traumTagData)
            continue
        }

        if (id === TraumTagTypes.POPULAR_CHOICE) {
            popularChoiceTag = traumTagData
        }

        if (id === TraumTagTypes.HIDDEN_GEM) {
            bottomTraumTags.push(traumTagData)
        }
    }

    return {
        badgeTraumTags,
        popularChoiceTag,
        bottomTraumTags,
        hasBottomTags: bottomTraumTags.length > 0,
    }
}
